import React from 'react'

import { Music, Calendar, Radio } from 'lucide-react'

import { Group } from '@/core/api/entities/group'
import { useTranslation } from '@/core/i18n/useTranslation'
import { AccountBadge } from '@/shared/components/atoms/AccountBadge/AccountBadge'
import { Card } from '@/shared/components/molecules/Card/Card'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

interface GroupCardProps {
	group: Group
	onClick?: (group: Group) => void
}

export const GroupCard: React.FC<GroupCardProps> = ({ group, onClick }) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const currentAccount = useCurrentAccount()

	const zoneCount = group.analytics?.players || 0
	const playlistsCount = group.directPlaylists ?? 0
	const schedulesCount = group.analytics?.schedules || 0
	const adCampaignsCount = group.analytics?.adverts || 0

	// Determine if this group has content assigned
	const hasContent =
		playlistsCount > 0 || schedulesCount > 0 || adCampaignsCount > 0

	return (
		<Card
			className="overflow-hidden cursor-pointer transition-all hover:shadow-lg group p-6 bg-card-dark border-border"
			onClick={() => onClick?.(group)}
			id={`group-card`}
			data-testid={`group-card`}
		>
			<div className="space-y-4">
				<div className="flex items-start justify-between">
					<div className="flex-1 min-w-0">
						{group.accountName && group.accountId !== currentAccount?.id && (
							<div className="mb-2">
								<AccountBadge accountName={group.accountName} />
							</div>
						)}
						<div className="text-sm text-muted-foreground mb-2">
							{zoneCount}{' '}
							{zoneCount === 1
								? t('card.zone')
								: tCommon('entities.zone.main_tabs.zones')}
						</div>
						<h4 className="font-normal text-xl line-clamp-1">{group.name}</h4>
					</div>
				</div>

				{hasContent ? (
					<div className="flex items-center gap-4 flex-wrap">
						{playlistsCount > 0 && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Music className="h-4 w-4 flex-shrink-0" />
								<span>
									{t('groupCard.playlists')} {playlistsCount}
								</span>
							</div>
						)}
						{schedulesCount > 0 && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Calendar className="h-4 w-4 flex-shrink-0" />
								<span>
									{t('groupCard.schedules')} {schedulesCount}
								</span>
							</div>
						)}
						{adCampaignsCount > 0 && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Radio className="h-4 w-4 flex-shrink-0" />
								<span>
									{t('groupCard.adCampaigns')} {adCampaignsCount}
								</span>
							</div>
						)}
					</div>
				) : (
					<div className="text-sm text-muted-foreground italic">
						{t('groupCard.noContent')}
					</div>
				)}
			</div>
		</Card>
	)
}

export default GroupCard
